import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { calculationWebSocketService } from 'services/calculationWebSocketService';
import classes from './JobProcessingProgressBar.module.scss';

interface JobProcessingProgressBarProps {
    /**
     * Room ID for which to show progress
     * Required for room-specific progress tracking
     */
    roomId: string;

    /**
     * Whether to show the progress bar
     * If not provided, will automatically show/hide based on WebSocket state for the specific room
     */
    show?: boolean;

    /**
     * Custom CSS class name for styling
     */
    className?: string;

    /**
     * Whether to auto-hide the progress bar after completion
     * Default: true
     */
    autoHide?: boolean;

    /**
     * Delay in milliseconds before hiding the progress bar after completion
     * Default: 3000 (3 seconds)
     */
    hideDelay?: number;

    /**
     * Whether to show the progress bar as a compact version
     * Default: false
     */
    compact?: boolean;
}

/**
 * Job Processing Progress Bar Component
 * 
 * This component displays real-time progress of job processing operations
 * by connecting to the job processing WebSocket service. It shows:
 * - Current progress percentage
 * - Status message
 * - Progress text (e.g., "5/10 jobs")
 * - Visual progress bar
 * - Completion status
 * 
 * The component automatically:
 * - Shows when processing starts
 * - Updates progress in real-time
 * - Shows completion status
 * - Auto-hides after completion (configurable)
 * 
 * Features:
 * - Real-time WebSocket integration
 * - Responsive design
 * - Compact and full-size modes
 * - Auto-hide functionality
 * - Error handling
 * - Accessibility support
 */
const JobProcessingProgressBar: React.FC<JobProcessingProgressBarProps> = ({
    roomId,
    show,
    className = '',
    autoHide = true,
    hideDelay = 3000,
    compact = false
}) => {
    const [shouldShow, setShouldShow] = useState(false);
    const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

    // Initialize WebSocket service on component mount
    useEffect(() => {
        calculationWebSocketService.initialize();

        // Cleanup on unmount
        return () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
        };
    }, []);

    // Handle auto-hide functionality
    useEffect(() => {
        if (autoHide && calculationWebSocketService.isRoomCompleted(roomId)) {
            // Clear any existing timeout
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }

            // Set new timeout to hide the progress bar
            const timeout = setTimeout(() => {
                setShouldShow(false);
                calculationWebSocketService.resetRoomProgress(roomId);
            }, hideDelay);

            setHideTimeout(timeout);
        }
    }, [calculationWebSocketService.isRoomCompleted(roomId), autoHide, hideDelay, roomId]);

    // Determine if progress bar should be shown
    const isVisible = show !== undefined ? show : (shouldShow || calculationWebSocketService.isRoomActive(roomId) || calculationWebSocketService.isRoomFailed(roomId));

    // Update shouldShow state based on WebSocket service
    useEffect(() => {
        if (calculationWebSocketService.isRoomActive(roomId) || calculationWebSocketService.isRoomFailed(roomId)) {
            setShouldShow(true);
        }
    }, [calculationWebSocketService.isRoomActive(roomId), calculationWebSocketService.isRoomFailed(roomId), roomId]);

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    const progressPercentage = calculationWebSocketService.getProgressPercentage(roomId);
    const statusMessage = calculationWebSocketService.getStatusMessage(roomId);
    const progressText = calculationWebSocketService.getProgressText(roomId);
    const isCompleted = calculationWebSocketService.isRoomCompleted(roomId);
    const isProcessing = calculationWebSocketService.isRoomActive(roomId);
    const isFailed = calculationWebSocketService.isRoomFailed(roomId);
    const failureData = calculationWebSocketService.getRoomFailure(roomId);

    // Get status icon based on current state
    const getStatusIcon = () => {
        if (isFailed) {
            return <AlertCircle size={16} className={classes.errorIcon} />;
        } else if (isCompleted) {
            return <CheckCircle size={16} className={classes.successIcon} />;
        } else if (isProcessing) {
            return <Clock size={16} className={classes.processingIcon} />;
        } else {
            return <AlertCircle size={16} className={classes.errorIcon} />;
        }
    };

    // Get status class based on current state
    const getStatusClass = () => {
        if (isFailed) {
            return classes.error;
        } else if (isCompleted) {
            return classes.completed;
        } else if (isProcessing) {
            return classes.processing;
        } else {
            return classes.error;
        }
    };

    if (compact) {
        // Compact version - just the progress bar with minimal text
        return (
            <div className={`${classes.progressBarContainer} ${classes.compact} ${className}`}>
                <div className={classes.compactContent}>
                    <div className={classes.progressInfo}>
                        {getStatusIcon()}
                        <span className={classes.statusText}>
                            {isFailed ? failureData?.status || 'Calculation Failed!' : statusMessage}
                        </span>
                        {!isFailed && <span className={classes.progressText}>({progressText})</span>}
                    </div>
                    <ProgressBar
                        value={progressPercentage}
                        className={`${classes.progressBar} ${getStatusClass()}`}
                        showValue={false}
                    />
                </div>
            </div>
        );
    }

    // Full version - card with detailed information
    return (
        <div className={`${classes.progressBarContainer} ${className}`}>
            <Card className={`${classes.progressCard} ${getStatusClass()}`}>
                <div className={classes.progressContent}>
                    <div className={classes.progressHeader}>
                        <div className={classes.statusInfo}>
                            {getStatusIcon()}
                            <span className={classes.statusMessage}>
                                {isFailed ? failureData?.status || 'Calculation Failed!' : statusMessage}
                            </span>
                        </div>
                        {!isFailed && (
                            <div className={classes.progressDetails}>
                                <span className={classes.progressText}>{progressText}</span>
                                <span className={classes.progressPercentage}>{progressPercentage}%</span>
                            </div>
                        )}
                    </div>

                    <div className={classes.progressBarWrapper}>
                        <ProgressBar
                            value={progressPercentage}
                            className={`${classes.progressBar} ${getStatusClass()}`}
                            showValue={false}
                        />
                    </div>

                    {isCompleted && (
                        <div className={classes.completionMessage}>
                            Job processing completed successfully!
                        </div>
                    )}

                    {isFailed && (
                        <div className={classes.errorMessage}>
                            <div className={classes.errorTitle}>Calculation Failed!</div>
                            <div className={classes.errorDetails}>
                                Job ID: {failureData?.jobId || 'Unknown'}
                            </div>
                            <div className={classes.errorAction}>
                                Save again to re-calculate
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default observer(JobProcessingProgressBar);
